import { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

import { verifyOtp } from "../../../service";
import { Context } from "../../../Provider";
import ResendCountdown from "../ResendCountdown";
import { theme } from "../../../../theme";
import BtnSpinner from "../../../../common/BtnSpinner";
import { formatPhoneNumber } from "../../../../utils";

const OTP_FIELDS = ['otp1', 'otp2', 'otp3', 'otp4', 'otp5', 'otp6'];

const OtpSubmit = ({ ftm_id, show, handleClose, showEdit, setShowEdit, newDetails }) => {
    const defaultValues = useMemo(() => Object.fromEntries(OTP_FIELDS.map(field => [field, ''])), []);
    
    const { register, handleSubmit, reset, setValue, getValues } = useForm({ defaultValues });
    const inputRefs = useRef([]);
    const [isLoading, setIsLoading] = useState(false);
    const { state, updateCurrentStep } = useContext(Context);
    const { ftm, step, commonSettings } = state;

    const currentStep = step?.stepId;
    const isPhone = currentStep === 1;

    useEffect(() => {
        if (show && inputRefs.current[0]) inputRefs.current[0].focus();
    }, [show]);

    const focusInput = useCallback(index => {
        inputRefs.current[index]?.focus();
    }, []);

    const handleInputChange = useCallback((e, index) => {
        const value = e.target.value;
        if (!/^\d?$/.test(value)) return;

        setValue(OTP_FIELDS[index], value);
        if (value && index < OTP_FIELDS.length - 1) {
            focusInput(index + 1);
        } else if (!value && index > 0) {
            focusInput(index - 1);
        }
    }, [setValue, focusInput]);

    const handleKeyDown = useCallback((e, index) => {
        if (e.key === 'Backspace' && !e.target.value && index > 0) {
            focusInput(index - 1);
        }
    }, [focusInput]);

    const getOtpCode = useCallback(() => OTP_FIELDS.map(field => getValues(field) || '').join(''), [getValues]);

    const onSubmitOtp = async () => {
        const otpCode = getOtpCode();
        try {
            setIsLoading(true);
            const response = await verifyOtp(ftm_id, otpCode, isPhone ? 1 : 2);

            if (response.status === 'Success') {
                if (!isPhone) handleClose?.();
                updateCurrentStep(isPhone ? 2 : 3);
                // optional: remove resend count here if needed
            } else {
                toast.error(response.message || "OTP verification failed");
            }
        } catch (error) {
            console.error("Error submitting OTP:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            reset();
            setIsLoading(false);
        }
    };

    const destination = isPhone
        ? formatPhoneNumber(
            newDetails?.phoneNumber || ftm?.ftm_contact_number,
            commonSettings?.phone_number_format ?? '+44 799 999 9999'
        )
        : newDetails?.email || ftm?.ftm_email;

    return (
        <form onSubmit={handleSubmit(onSubmitOtp)} className="m-2">
            <div className="row gx-2 gx-sm-3 mb-3">
                <h5 className="mb-1 text-gradient text-dark">
                    <i className="material-icons text-lg me-2">{isPhone ? 'call' : 'mark_email_unread'}</i>
                    {isPhone ? 'PHONE NUMBER' : 'EMAIL'} VERIFICATION CODE
                </h5>
                <p className="mb-2 text-sm">
                    A verification code has been sent to your {isPhone ? 'phone number' : 'email address'} ({destination}).
                </p>

                {OTP_FIELDS.map((field, index) => {
                    const { ref, ...rest } = register(field);
                    return (
                        <div className="col" key={field}>
                            <div className="input-group input-group-outline">
                                <input
                                    type="text"
                                    maxLength={1}
                                    autoComplete="off"
                                    className="form-control form-control-lg text-center"
                                    {...rest}
                                    ref={el => {
                                        ref(el);
                                        inputRefs.current[index] = el;
                                    }}
                                    onChange={e => handleInputChange(e, index)}
                                    onKeyDown={e => handleKeyDown(e, index)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <ResendCountdown ftm={ftm} currentStep={currentStep} setShowEdit={setShowEdit} />

            <div className="d-flex justify-content-center gap-2">
                <button
                    type="submit"
                    className={`btn btn-sm bg-gradient-${theme.main} w-100 my-4 mb-0`}
                    disabled={isLoading}
                >
                    {isLoading ? <BtnSpinner /> : showEdit ? 'SUBMIT' : 'VERIFY'}
                </button>
            </div>
        </form>
    );
};

export default OtpSubmit;